import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchUserDevices, deleteUserDevice } from "../../redux/account";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import "../../components/styles/manageDevicePage.scss";

/**
 * ManageDevicePage — Manage connected devices
 * Figma: Manage Device settings
 *
 * Fetches real devices from Client API, allows removing devices.
 */

const ManageDevicePage = () => {
  const navigate = useNavigate();
  const { devices } = useSelector((state) => state.account);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  // Current device ID from localStorage (set during login)
  const currentDeviceId = localStorage.getItem("mango_device_id_server");

  // Helper: safely extract a string from a value that might be an object
  const safeStr = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string") return val;
    if (typeof val === "number") return String(val);
    if (typeof val === "object") {
      return val.uid || val.name || val.value || JSON.stringify(val);
    }
    return String(val);
  };

  useEffect(() => {
    const loadDevices = async () => {
      setIsLoading(true);
      setError(null);
      const result = await fetchUserDevices();
      if (!result) {
        setError("Unable to load devices. Please try again.");
      }
      setIsLoading(false);
    };
    loadDevices();
  }, []);

  const handleRemoveDevice = useCallback(
    async (deviceId) => {
      if (deletingId) return;
      setDeletingId(deviceId);
      setError(null);

      const success = await deleteUserDevice(deviceId);
      if (!success) {
        setError("Failed to remove device. Please try again.");
      }
      setDeletingId(null);
    },
    [deletingId]
  );

  const getDeviceIcon = (device) => {
    const deviceClass = safeStr(device.device_class).toUpperCase();

    if (deviceClass === "MOBILE" || deviceClass === "PHONE") {
      return (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="manage-device-page__device-icon">
          <rect x="8" y="2" width="16" height="28" rx="2" stroke="white" strokeWidth="1.5" />
          <circle cx="16" cy="26" r="1.5" fill="white" />
        </svg>
      );
    }

    if (deviceClass === "TABLET") {
      return (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="manage-device-page__device-icon">
          <rect x="4" y="2" width="24" height="28" rx="2" stroke="white" strokeWidth="1.5" />
          <circle cx="16" cy="26" r="1.5" fill="white" />
        </svg>
      );
    }

    if (deviceClass === "STB" || deviceClass === "TV" || deviceClass === "SMARTTV") {
      return (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="manage-device-page__device-icon">
          <rect x="2" y="4" width="28" height="18" rx="2" stroke="white" strokeWidth="1.5" />
          <path d="M8 22h16" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M12 26h8" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    }

    // Default: desktop/laptop
    return (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="manage-device-page__device-icon">
        <rect x="2" y="2" width="28" height="18" rx="1" stroke="white" strokeWidth="1.5" />
        <path d="M8 20h16" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M10 26h12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  };

  const getDeviceName = (device) => {
    const uid = safeStr(device.uid);
    const type = safeStr(device.device_type);
    const os = safeStr(device.device_os);
    const cls = safeStr(device.device_class);
    const model = safeStr(device.device_model || device.model);
    const name = safeStr(device.device_name || device.name);

    // Prefer explicit name or uid (GloTV pattern)
    if (name) return name;
    if (model) return model;

    // Parse user-agent style device_type into readable name
    if (type) {
      // Try to extract browser name from strings like "Safari 604.1", "Chrome 120.0"
      const browserMatch = type.match(/^(Chrome|Firefox|Safari|Edge|Opera|Brave|Samsung)[\s/]?/i);
      if (browserMatch) {
        const browser = browserMatch[1];
        if (os) return `${browser} on ${os}`;
        if (cls) return `${browser} · ${cls}`;
        return browser;
      }
    }

    // Build from OS + class
    if (os && cls) return `${os} · ${cls}`;
    if (os) return os;

    // Use uid as last resort (like GloTV does)
    if (uid) return uid;
    if (cls) return cls;
    return "Unknown Device";
  };

  return (
    <div className="manage-device-page">
      <Navbar activeTab="" />

      <div className="manage-device-page__body">
        {/* Breadcrumb */}
        <div className="manage-device-page__breadcrumb">
          <span
            className="manage-device-page__breadcrumb-link"
            onClick={() => navigate("/settings")}
            style={{ cursor: "pointer" }}
          >
            Setting
          </span>
          <span className="manage-device-page__breadcrumb-sep">›</span>
          <span className="manage-device-page__breadcrumb-current">
            Manage device
          </span>
        </div>

        {/* Card */}
        <div className="manage-device-page__card">
          <h2 className="manage-device-page__card-title">Manage device</h2>

          {/* Loading state */}
          {isLoading && (
            <p className="manage-device-page__loading">Loading devices...</p>
          )}

          {/* Error */}
          {error && (
            <p className="manage-device-page__error">{error}</p>
          )}

          {/* Empty state */}
          {!isLoading && !error && devices.length === 0 && (
            <p className="manage-device-page__empty">No devices registered.</p>
          )}

          {/* Device rows */}
          {!isLoading && devices.length > 0 && (
            <div className="manage-device-page__device-list">
              {devices.map((device, index) => {
                const isCurrentDevice =
                  String(device.id) === String(currentDeviceId);

                return (
                  <div
                    key={device.id || index}
                    className="manage-device-page__device-row"
                  >
                    <div className="manage-device-page__device-icon-wrapper">
                      {getDeviceIcon(device)}
                    </div>

                    <div className="manage-device-page__device-info">
                      <div className="manage-device-page__device-name-wrapper">
                        <span className="manage-device-page__device-name">
                          {getDeviceName(device)}
                        </span>
                        {isCurrentDevice && (
                          <span className="manage-device-page__device-tag">
                            This Device
                          </span>
                        )}
                      </div>
                      <span className="manage-device-page__device-subtitle">
                        {safeStr(device.device_class) || ""}
                        {device.device_os ? ` · ${safeStr(device.device_os)}` : ""}
                        {device.last_login_at
                          ? ` · Last active ${new Date(device.last_login_at).toLocaleDateString()}`
                          : device.updated_at
                            ? ` · Last active ${new Date(device.updated_at).toLocaleDateString()}`
                            : ""}
                      </span>
                    </div>

                    <button
                      className="manage-device-page__logout-btn"
                      onClick={() => handleRemoveDevice(device.id)}
                      disabled={deletingId === device.id}
                    >
                      {deletingId === device.id ? "Removing..." : "Remove"}
                    </button>

                    {index < devices.length - 1 && (
                      <div className="manage-device-page__divider"></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ManageDevicePage;
